import ApiService from "@/api/ApiService";
import type { AccountResponse, GetAccountsRequest, StockPositionResponse, CoinPositionResponse, FundPositionResponse, PagedResponse, NoteResponse, OptionPriceResponse, GetOptionPriceRequest, GetStockPriceRequest, GetStockPriceResponse } from "@/api/ApiTypes";
import { OptionRightEnum } from "@/api/ApiTypes";
import FMPApiService from "@/api/FMPApiService";
import type { MarketHours } from "@/api/types";
import { isMarketOpenNow } from "@/utils/marketHours";
import type { EnrichedStockPosition, EnrichedOptionPosition } from "@/types/positions";
import type { EnrichedCoinPosition } from "@/types/coin";
import type { EnrichedFundPosition } from "@/types/fund";
import type { EnrichedAccount } from "@/types/ultimate";
import type { NoteOwnerType, CreateNoteRequest, AddAttachmentRequest, NoteAttachmentDto } from "@/api/ApiTypes";
import { NoteType, NoteContextType } from "@/api/ApiTypes";
import { defineStore } from "pinia";
import { ref } from "vue";

// Shared types exported for components to consume
export interface StockPrice {
    symbol: string
    price: number
    change: number
    changePercent: number
    previousClose: number
    lastUpdated: Date
    isLoading: boolean
    error: string | null
    isMarketOpen?: boolean
    exchange?: string
    currency?: string | null
}

export interface ExchangeStatus {
    exchange: string
    isOpen: boolean
    lastChecked: Date
    marketHours?: MarketHours
}

export const useMyStore = defineStore('mystore', () => {
    // Services
    const api = new ApiService(
        import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api',
        localStorage.getItem('auth_token_for_mani') || undefined
    )

    // FMP API for prices/market data
    const fmpApiKey = import.meta.env.VITE_FMP_API_KEY || ''
    const fmp = new FMPApiService(fmpApiKey)

    const trackedMarkets = ref<Set<string>>(new Set())
    // Basic exchange → currency inference as a fallback when profile currency is unavailable
    const inferCurrencyFromExchange = (exchange: string | null | undefined): string | null => {
        if (!exchange) return null
        const ex = exchange.toUpperCase()
        // Common mappings; extend as needed
        if (ex === 'IST' || ex === 'BIST' || ex === 'XIST') return 'TRY'
        if (ex === 'LSE' || ex === 'XLON') return 'GBX'
        if (ex === 'FWB' || ex === 'XFRA' || ex === 'XETR' || ex === 'XETRA') return 'EUR'
        if (ex === 'TSE' || ex === 'XTSE' || ex === 'TSX') return 'CAD'
        if (ex === 'JPX' || ex === 'TSEJ' || ex === 'XJPX' || ex === 'XTKS') return 'JPY'
        if (ex === 'NYSE' || ex === 'NASDAQ' || ex === 'XNYS' || ex === 'XNAS') return 'USD'
        return null
    }
    // Profile fetch de-duplication caches
    const fetchedProfiles = ref<Set<string>>(new Set())
    const profileRequests = ref<Map<string, Promise<void>>>(new Map())

    const accounts = ref<EnrichedAccount[]>([])
    const positions = ref<EnrichedStockPosition[]>([])
    const stockPrices = ref<Map<string, StockPrice>>(new Map())
    const optionPrices = ref<Map<string, { price: number | null; lastUpdated: Date | null; error: string | null }>>(new Map())
    // Option positions (aggregated per contract/code)
    const optionPositions = ref<EnrichedOptionPosition[]>([])
    const currencyPrices = ref<Map<string, StockPrice>>(new Map())
	// Coin positions and prices
	const coinPositions = ref<EnrichedCoinPosition[]>([])
	const coinPrices = ref<Map<string, StockPrice>>(new Map())
    // Fund positions and prices (fund last price is numeric; backend source)
    const fundPositions = ref<EnrichedFundPosition[]>([])
    const fundPrices = ref<Map<string, number>>(new Map())

    // Option pricing source switch: false -> Yahoo Finance proxy (existing), true -> Backend endpoint
    const useServerOptionPrice = ref<boolean>(true)
    const setUseServerOptionPrice = (value: boolean): void => { useServerOptionPrice.value = !!value }

    const exchangeStatuses = ref<Map<string, ExchangeStatus>>(new Map())
    const didInitialExchangeRefresh = ref<boolean>(false)
    const didInitialOptionsFetch = ref<boolean>(false)

    // Cadences
    const STOCKS_INTERVAL_MS = 30_000
    const OPTIONS_INTERVAL_MS = 60_000
    const CURRENCIES_INTERVAL_MS = 5 * 60_000
    const FUNDS_INTERVAL_MS = 5 * 60_000
    const MARKETS_INTERVAL_MS = 60 * 60_000 // 1 hour — API refresh for holiday detection
    const LOCAL_MARKET_CHECK_MS = 60_000   // 1 minute — client-side open/close check
    const CLOSED_MARKET_REFRESH_MS = 15 * 60_000
    const BATCH_SIZE = 100

    // Polling intervals
    const stocksInterval = ref<ReturnType<typeof setInterval> | null>(null)
    const optionsInterval = ref<ReturnType<typeof setInterval> | null>(null)
    const currenciesInterval = ref<ReturnType<typeof setInterval> | null>(null)
    const marketsInterval = ref<ReturnType<typeof setInterval> | null>(null)
    const localMarketCheckInterval = ref<ReturnType<typeof setInterval> | null>(null)
    const fundsInterval = ref<ReturnType<typeof setInterval> | null>(null)

    // UI: Global drawers/modals
    const notesDrawerOpen = ref<boolean>(false)
    const notesOwnerType = ref<NoteOwnerType | null>(null)
    const notesOwnerId = ref<number | null>(null)
    const notes = ref<NoteResponse[]>([])
    const notesTotal = ref<number>(0)
    const notesIsLoading = ref<boolean>(false)
    const notesError = ref<string | null>(null)
    const notesPage = ref<number>(1)
    const notesPageSize = ref<number>(50)
    const openNotesDrawer = async (ownerType: NoteOwnerType, ownerId: number): Promise<void> => {
        notesOwnerType.value = ownerType
        notesOwnerId.value = ownerId
        notesDrawerOpen.value = true
        await loadNotes(1, notesPageSize.value)
    }
    const closeNotesDrawer = (): void => {
        notesDrawerOpen.value = false
        notesOwnerType.value = null
        notesOwnerId.value = null
    }
    const toggleNotesDrawer = (): void => { notesDrawerOpen.value = !notesDrawerOpen.value }

    // Notes fetcher
    const loadNotes = async (page: number = 1, pageSize: number = notesPageSize.value): Promise<void> => {
        if (!notesOwnerType.value || !notesOwnerId.value) return
        notesIsLoading.value = true
        notesError.value = null
        try {
            const req: any = {
                pageNumber: page,
                pageSize: pageSize,
                sortBy: null,
                sortDesc: null,
                searchTerm: null,
                ownerType: notesOwnerType.value,
                ownerId: notesOwnerId.value,
                noteType: null,
                tags: null,
                from: null,
                to: null,
                includeDeleted: false,
            }
            const resp = await api.post<PagedResponse<NoteResponse>>('/notes/GetAll', req)
            if (resp.success && resp.data) {
                notes.value = resp.data.items || []
                notesTotal.value = resp.data.totalCount || 0
                notesPage.value = resp.data.pageNumber || page
                notesPageSize.value = resp.data.pageSize || pageSize
            } else {
                throw new Error(resp.message || 'Failed to load notes')
            }
        } catch (e: any) {
            notesError.value = e?.message || 'Network error'
        } finally {
            notesIsLoading.value = false
        }
    }

    type BinaryAttachment = { fileName?: string | null; contentType?: string | null; sizeBytes?: number | null; binaryBase64?: string | null; url?: string | null }

    const createNote = async (content: string, options?: { title?: string | null; noteType?: NoteType | null; urlAttachments?: NoteAttachmentDto[]; binaries?: BinaryAttachment[] }): Promise<number | null> => {
        if (!notesOwnerType.value || !notesOwnerId.value) return null
        const body: CreateNoteRequest = {
            ownerType: notesOwnerType.value,
            ownerId: notesOwnerId.value,
            noteType: options?.noteType ?? NoteType.General,
            title: options?.title || null,
            content: (content || '').trim(),
            contextType: NoteContextType.Other,
            snapshotPrice: null,
            snapshotAmount: null,
            snapshotCurrency: null,
            snapshotValueInUsd: null,
            snapshotExchangeRateId: null,
            snapshotAsOf: null,
            snapshotSource: 'UI',
            attachments: options?.urlAttachments?.length ? options.urlAttachments : null,
        }
        const resp = await api.post<number>('/notes/Create', body as any)
        if (!resp.success || !resp.data) throw new Error(resp.message || 'Failed to create note')
        const noteId = resp.data
        const binaries = options?.binaries || []
        for (const b of binaries) {
            if (!b?.binaryBase64) continue
            const attach: AddAttachmentRequest = {
                noteId,
                url: b.url ?? null,
                contentType: b.contentType ?? null,
                fileName: b.fileName ?? null,
                sizeBytes: b.sizeBytes ?? null,
                binaryBase64: b.binaryBase64 ?? null,
            }
            await api.post<number>('/notes/AddAttachment', attach as any)
        }
        await loadNotes(1, notesPageSize.value)
        return noteId
    }

    // UI: NoteType badge colors (Tailwind classes)
    const noteTypeBadgeClassMap: Record<NoteType, string> = {
        [NoteType.General]: 'bg-gray-100 text-gray-600 dark:bg-gray-400/10 dark:text-gray-400',
        [NoteType.Research]: 'bg-blue-100 text-blue-700 dark:bg-blue-400/10 dark:text-blue-400',
        [NoteType.Idea]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-400/10 dark:text-yellow-500',
        [NoteType.Warning]: 'bg-red-100 text-red-700 dark:bg-red-400/10 dark:text-red-400',
        [NoteType.Journal]: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-400',
        [NoteType.Todo]: 'bg-green-100 text-green-700 dark:bg-green-400/10 dark:text-green-400',
        [NoteType.Compliance]: 'bg-purple-100 text-purple-700 dark:bg-purple-400/10 dark:text-purple-400',
    }
    const getNoteTypeBadgeClasses = (t: NoteType | number): string => noteTypeBadgeClassMap[t as NoteType] || noteTypeBadgeClassMap[NoteType.General]

    async function loadAccounts(): Promise<void> {
        const req: GetAccountsRequest = {
            pageNumber: 1,
            pageSize: 1000,
            sortBy: null,
            sortDesc: null,
            searchTerm: null,
            includeDeleted: false,
        }
        console.log('getAllAccounts2')
        const resp = await api.post<AccountResponse[]>('/account/GetAll', req)
        if (resp.success && resp.data) {
            accounts.value = resp.data.map(a => ({ ...a, stockMarketValue: 0, totalValue: a.amount || 0, enrichedStockPositionLots: a.stockPositionLots?.map(l=>({...l, currentValue: null})) }))

            const currencies = [
                ...new Set(
                    accounts.value
                        .map(a => "USD" + a.currency)
                        .filter(c => c && c !== "USDUSD") // boş olmayan ve USD olmayanlar
                )
            ]
            if (currencies.length > 0) {
                currencies.forEach(c => currencyPrices.value.set(c, null))
            }

            fetchMultipleCurrencies(currencies);
            // Compute initial USD valuations if possible (may refine after prices/rates arrive)
            recomputeUsdValuations()
        } else {
            throw new Error(resp.message || 'Failed to load accounts')
        }
    }

    async function loadPositions(includeClosed: boolean = false): Promise<void> {
        const resp = await api.post<StockPositionResponse[]>('/stockposition/GetAll', {
            includeClosed,
            sortBy: null,
            sortDescending: false,
        })
        if (resp.success && resp.data) {
            positions.value = resp.data.map((p) => {
                return {
                    ...p,
                    exchange: null,
                    marketOpen: null,
                }
            })
            // Track symbols for live pricing
            const tickers = resp.data.map(p => p.ticker?.toUpperCase()).filter(Boolean)
            if (tickers.length > 0) await trackMultipleSymbols(tickers)
        } else {
            throw new Error(resp.message || 'Failed to load positions')
        }
    }

    // Option positions loader
    async function loadOptionPositions(includeClosed: boolean = false): Promise<void> {
        console.log('[Options] loadOptionPositions start', { includeClosed })
        const resp = await api.post<any[]>('/optionposition/GetAll', {
            includeClosed,
            sortBy: null,
            sortDescending: false,
        } as any)
        if (resp.success && resp.data) {
            const arr = (Array.isArray(resp.data) ? resp.data : []) as any[]
            optionPositions.value = arr.map((p: any) => ({
                ...p,
                avgCostUsd: Number(p?.averageCostInUsd ?? p?.averagePriceInUsd ?? p?.averagePriceUsd ?? 0) || 0,
                investedUsd: Number(p?.investedInUsd ?? p?.remainingCostInUsd ?? 0) || 0,
                realizedUsd: Number(p?.realizedProfitLossInUsd ?? p?.realizedPlInUsd ?? p?.realizedUsd ?? 0) || 0,
                createdDate: p?.createdDate ?? p?.firstTradeDate ?? null,
            }))
            console.log('[Options] loadOptionPositions done', { count: optionPositions.value.length })
            const codeSamples = optionPositions.value.map((p: any) => p?.code).filter(Boolean).slice(0, 5)
            if (codeSamples.length) console.log('[Options] code samples', codeSamples)
            // rebuild enriched option lots per account
            rebuildEnrichedOptionLotsWithPrices()
            // update per-position price field
            updateOptionPositionsPrices()
            // ensure account valuations reflect option lots
            recomputeUsdValuations()
        } else {
            throw new Error(resp.message || 'Failed to load option positions')
        }
    }

	// Coin positions loader
	async function loadCoinPositions(): Promise<void> {
		const resp = await api.post<CoinPositionResponse[]>('/coinposition/GetAll', {
			includeClosed: false,
			sortBy: null,
			sortDescending: false,
		})
		if (resp.success && resp.data) {
			coinPositions.value = resp.data.map((p) => {
				return {
					...p,
					// normalized aliases for stock-based UI
					ticker: (p.symbol || '').toUpperCase(),
					stockName: p.coinName,
					stockTickerId: p.coinTickerId as unknown as number,
					exchange: null,
					marketOpen: null,
				}
			})
			const tickers = resp.data.map(p => (p.symbol || '').toUpperCase()).filter(Boolean)
			if (tickers.length > 0) await trackMultipleCoinSymbols(tickers)
		} else {
			throw new Error(resp.message || 'Failed to load coin positions')
		}
	}

    // Fund positions loader
    async function loadFundPositions(): Promise<void> {
        const resp = await api.post<FundPositionResponse[]>('/fundposition/GetAll', {
            includeClosed: false,
            sortBy: null,
            sortDescending: false,
        })
        if (resp.success && resp.data) {
            fundPositions.value = resp.data.map((p: FundPositionResponse) => {
                const lotCurrency = Array.isArray(p.lots) && p.lots.length > 0
                    ? (p.lots.find((l: any) => l.isOpen)?.account?.currency || p.lots[0].account?.currency)
                    : 'USD'
                const currency = (lotCurrency || 'USD')
                return {
                    ...p,
                    exchange: null,
                    marketOpen: null,
                    currency,
                    price: null,
                    change: null,
                    changePercent: null,
                } as EnrichedFundPosition
            })
            const codes = resp.data.map((p: any) => (p.code || '').toUpperCase()).filter((x: string) => !!x)
            if (codes.length > 0) await fetchMultipleFundLastPrices(codes)
        } else {
            throw new Error(resp.message || 'Failed to load fund positions')
        }
    }

    const trackMultipleSymbols = async (symbols: string[]) => {
        if (!fmpApiKey) return
        const uppers = symbols.map(s => s.toUpperCase())
        uppers.forEach(s => stockPrices.value.set(s, null))
        await fetchMultiplePrices(uppers)
        ensurePollingStarted()
    }

// will be defined after fetchMultipleCoinPrices


    const getCompanyProfile = async (symbol: string) => {
        // If we've already fetched once successfully, skip
        if (fetchedProfiles.value.has(symbol)) return
        // Coalesce concurrent requests per symbol
        const inflight = profileRequests.value.get(symbol)
        if (inflight) { await inflight; return }

        const req = (async () => {
            const response = await fmp.getCompanyProfile(symbol)
            if (response.success && response.data && response.data.length > 0) {
                const profile = response.data[0]
                const current = stockPrices.value.get(symbol)
                stockPrices.value.set(symbol, {
                    ...current,
                    currency: profile.currency,
                })
                positions.value.forEach(p => {
                    if (p.ticker === symbol) {
                        p.currency = profile.currency
                    }
                })
                // Mark as fetched to avoid future calls
                fetchedProfiles.value.add(symbol)
            }
        })()
        profileRequests.value.set(symbol, req)
        try { await req } finally { profileRequests.value.delete(symbol) }
    }
    // Price fetching
    const fetchMultiplePrices = async (symbols: string[]) => {
        if (!symbols || symbols.length === 0) return
        try {
            const uppers = symbols.map(s => s.toUpperCase())

            const response = await fmp.getQuotes(uppers)
            if (response.success && response.data) {
                
                const returnedSymbols = new Set(response.data.map(q => q.symbol.toUpperCase()))
                
                const missingSymbols = uppers.filter(s => !returnedSymbols.has(s))
                if (missingSymbols.length > 0) {
                    console.warn('[fetchMultiplePrices] Missing tickers, falling back to server:', missingSymbols)
                    await Promise.all(missingSymbols.map(async (sym) => {
                        try {
                            const req: GetStockPriceRequest = { symbol: sym }
                            const resp = await api.post<GetStockPriceResponse>('/stockticker/GetPrice', req)
                            if (resp.success && resp.data) {
                                const d = resp.data
                                const existing = stockPrices.value.get(sym)
                                const change = d.regularMarketPrice - d.regularMarketPreviousClose
                                const changePercent = d.regularMarketPreviousClose ? (change / d.regularMarketPreviousClose) * 100 : 0
                                const sp: StockPrice = {
                                    symbol: d.symbol || sym,
                                    price: d.regularMarketPrice,
                                    change,
                                    changePercent,
                                    previousClose: d.regularMarketPreviousClose,
                                    lastUpdated: new Date(),
                                    isLoading: false,
                                    error: null,
                                    isMarketOpen: existing?.isMarketOpen ?? null,
                                    exchange: d.exchange || null,
                                    currency: (d.currency?.replace(/=.*$/, '') || existing?.currency) ?? inferCurrencyFromExchange(d.exchange) ?? null,
                                }
                                stockPrices.value.set(sym, sp)
                                if (d.exchange) trackedMarkets.value.add(d.exchange.toUpperCase())
                                positions.value.filter(p => p.ticker === sym).forEach(p => {
                                    p.exchange = sp.exchange
                                    p.price = sp.price
                                    p.change = sp.change
                                    p.changePercent = sp.changePercent
                                    p.currency = sp.currency
                                    accounts.value.forEach(a => a.enrichedStockPositionLots.forEach(l => {
                                        if (l.stockPositionId === p.id) {
                                            l.currentValue = sp.price * l.remainingQuantity
                                        }
                                    }))
                                })
                                console.log('[fetchMultiplePrices] Server fallback OK:', sym, d.regularMarketPrice)
                            }
                        } catch (e: any) {
                            console.error('[fetchMultiplePrices] Server fallback failed for', sym, e?.message)
                        }
                    }))
                }

                response.data.forEach((quote) => {
                    const upperSymbol = quote.symbol.toUpperCase()
                    const existing = stockPrices.value.get(upperSymbol)
                    const sp: StockPrice = {
                        symbol: quote.symbol,
                        price: quote.price,
                        change: quote.change,
                        changePercent: quote.changePercentage,
                        previousClose: quote.previousClose,
                        lastUpdated: new Date(),
                        isLoading: false,
                        error: null,
                        isMarketOpen: existing?.isMarketOpen ?? null,
                        exchange: quote.exchange,
                        // Try to preserve or infer currency to avoid mislabeling USD
                        currency: existing?.currency ?? inferCurrencyFromExchange(quote.exchange) ?? null,
                    }
                    stockPrices.value.set(upperSymbol, sp)

                    if (sp.currency === null) {
                        getCompanyProfile(sp.symbol);
                    }

                    if (quote.exchange) {
                        const ex = quote.exchange.toUpperCase()
                        trackedMarkets.value.add(ex)
                    }



                    positions.value.filter(p => p.ticker === upperSymbol).forEach(p => {
                        p.exchange = sp.exchange
                        p.price = sp.price
                        p.change = sp.change
                        p.changePercent = sp.changePercent
                        p.currency = sp.currency

                        accounts.value.forEach(a=>a.enrichedStockPositionLots.forEach(l=>{
                            if(l.stockPositionId===p.id){
                                l.currentValue = sp.price * l.remainingQuantity
                            }
                        }
                        ))
                    })
                })
                // After price updates, recompute account USD valuations
                recomputeUsdValuations()
                // osman
                if (!didInitialExchangeRefresh.value) {
                    const exchanges = new Set<string>()
                    trackedMarkets.value.forEach(ex => exchanges.add(ex.toUpperCase()))
                    await Promise.all(Array.from(exchanges).map(ex => updateExchangeStatus(ex)))
                    didInitialExchangeRefresh.value = true
                }

            } else {
                throw new Error(response.message || 'Failed to fetch prices')
            }
        } catch (err: any) {
            //priceError.value = err?.message || 'Failed to fetch prices'
        } finally {
            //priceIsLoading.value = false
        }
    }

	// Price fetching (FX pairs)
    const fetchMultipleCurrencies = async (symbols: string[]) => {
        ""
        if (!symbols || symbols.length === 0) return
        try {
            const uppers = symbols.map(s => s.toUpperCase())

            const response = await fmp.getQuotes(uppers)
            if (response.success && response.data) {

                response.data.forEach((quote) => {
                    const upperSymbol = quote.symbol.toUpperCase()
                    const existing = stockPrices.value.get(upperSymbol)
                    const sp: StockPrice = {
                        symbol: quote.symbol,
                        price: quote.price,
                        change: quote.change,
                        changePercent: quote.changePercentage,
                        previousClose: quote.previousClose,
                        lastUpdated: new Date(),
                        isLoading: false,
                        error: null,
                        isMarketOpen: existing?.isMarketOpen ?? null,
                        exchange: quote.exchange,
                        // preserve previously fetched currency to avoid repeated profile fetches
                        currency: existing?.currency ?? null,
                    }
                    currencyPrices.value.set(upperSymbol, sp)
                    // osman




                })

                // Recompute after currency updates
                recomputeUsdValuations()


            } else {
                throw new Error(response.message || 'Failed to fetch prices')
            }
        } catch (err: any) {
            //priceError.value = err?.message || 'Failed to fetch prices'
        } finally {
            //priceIsLoading.value = false
        }
    }

    // Coin price fetching
    const fetchMultipleCoinPrices = async (symbols: string[]) => {
        if (!symbols || symbols.length === 0) return
        try {
            const uppers = symbols.map(s => s.toUpperCase())
            const response = await fmp.getQuotes(uppers)
            if (response.success && response.data) {
                response.data.forEach((quote) => {
                    const upperSymbol = quote.symbol.toUpperCase()
                    const existing = coinPrices.value.get(upperSymbol)
                    const sp: StockPrice = {
                        symbol: quote.symbol,
                        price: quote.price,
                        change: quote.change,
                        changePercent: quote.changePercentage,
                        previousClose: quote.previousClose,
                        lastUpdated: new Date(),
                        isLoading: false,
                        error: null,
                        isMarketOpen: existing?.isMarketOpen ?? null,
                        exchange: quote.exchange,
                        currency: existing?.currency ?? null,
                    }
                    coinPrices.value.set(upperSymbol, sp)
                    if (quote.exchange) {
                        const ex = quote.exchange.toUpperCase()
                        trackedMarkets.value.add(ex)
                    }
                    coinPositions.value.filter(p => p.ticker === upperSymbol).forEach(p => {
                        p.exchange = sp.exchange
                        p.price = sp.price
                        p.change = sp.change
                        p.changePercent = sp.changePercent
                        p.currency = sp.currency || p.currency || 'USD'
                    })
                })
                // After coin price updates, recompute account USD valuations
                recomputeUsdValuations()
            } else {
                throw new Error(response.message || 'Failed to fetch coin prices')
            }
        } catch (err: any) {
            // no-op
        } finally {
            // no-op
        }
    }

    const trackMultipleCoinSymbols = async (symbols: string[]) => {
        if (!fmpApiKey) return
        const uppers = symbols.map(s => s.toUpperCase())
        uppers.forEach(s => coinPrices.value.set(s, null))
        await fetchMultipleCoinPrices(uppers)
        ensurePollingStarted()
    }

    // Fund price fetching from backend (GetLastPrice per code)
    const fetchMultipleFundLastPrices = async (codes: string[]) => {
        if (!codes || codes.length === 0) return
        try {
            const tasks = codes.map(async (code) => {
                const req = { code, lookbackDays: 0 }
                const resp = await api.post<{ code: string; name: string | null; date: any; price: number }>(
                    '/fundticker/GetLastPrice',
                    req
                )
                if (resp.success && resp.data) {
                    fundPrices.value.set(code, resp.data.price)
                    fundPositions.value.filter(fp => fp.code === code).forEach(fp => {
                        fp.price = resp.data.price
                    })
                }
            })
            await Promise.all(tasks)
            recomputeUsdValuations()
        } catch (err: any) {
            // no-op
        }
    }


    const updateExchangeStatus = async (exchange: string) => {
        const upperExchange = exchange.toUpperCase()
        try {

            const mh = await fetchExchangeMarketHours(upperExchange)
            if (mh) {
                const exStatus: ExchangeStatus = {
                    exchange: upperExchange,
                    isOpen: mh.isMarketOpen,
                    lastChecked: new Date(),
                    marketHours: mh,
                }
                exchangeStatuses.value.set(upperExchange, exStatus)
                // Update all related prices' market flag
                stockPrices.value.forEach((p) => {
                    if (p.exchange && p.exchange.toUpperCase() === upperExchange) {
                        p.isMarketOpen = mh.isMarketOpen
                    }
                })
                positions.value.forEach((p) => {
                    if (p.exchange && p.exchange.toUpperCase() === upperExchange) {
                        p.marketOpen = mh.isMarketOpen;
                    }
                })
                return mh.isMarketOpen
            }
            return false

        } catch (err) {
            console.error(`Failed to update exchange status for ${upperExchange}:`, err)
            return false
        } finally {
            //exchangeUpdatePromises.value.delete(upperExchange)
        }
    }

    // Client-side market status refresh using cached schedule (no API call)
    const refreshMarketStatusLocally = () => {
        exchangeStatuses.value.forEach((status, exchange) => {
            if (!status.marketHours) return
            const isOpen = isMarketOpenNow(status.marketHours)
            if (status.isOpen === isOpen) return
            status.isOpen = isOpen
            // Cascade to prices and positions
            stockPrices.value.forEach((p) => {
                if (p.exchange && p.exchange.toUpperCase() === exchange) {
                    p.isMarketOpen = isOpen
                }
            })
            positions.value.forEach((p) => {
                if (p.exchange && p.exchange.toUpperCase() === exchange) {
                    p.marketOpen = isOpen
                }
            })
            coinPrices.value.forEach((p) => {
                if (p.exchange && p.exchange.toUpperCase() === exchange) {
                    p.isMarketOpen = isOpen
                }
            })
        })
    }

    // Exchange management
    const fetchExchangeMarketHours = async (exchange: string) => {
        try {
            const response = await fmp.getExchangeMarketHours(exchange)
            if (response.success && response.data) return response.data[0]
        } catch (err) {
            console.error(`Failed to fetch market hours for ${exchange}:`, err)
        }
        return null
    }

    // Helpers for options tracking
    const parseOptionKeyFromCode = (code?: string | null): { symbol: string; epoch: number; strike: number; right: 'C' | 'P' } | null => {
        if (!code) return null
        const re = /^([A-Z0-9\.\-]+)\s+(\d{4}-\d{2}-\d{2})\s+(\d+(?:\.\d+)?)\s*([CP])$/i
        const m = code.trim().toUpperCase().match(re)
        if (!m) return null
        const symbol = m[1]
        const dateStr = m[2]
        const strike = Number(m[3])
        const right = (m[4] as 'C' | 'P')
        const epoch = Math.floor(new Date(dateStr + 'T00:00:00Z').getTime() / 1000)
        if (!Number.isFinite(epoch) || !Number.isFinite(strike)) return null
        return { symbol, epoch, strike, right }
    }

    const buildOptionKey = (symbol: string, epoch: number, strike: number, right: string): string => `${symbol}|${epoch}|${strike}|${right}`

    // Treat expiration as end-of-day for the given epoch date (00:00Z + 1 day - 1ms)
    const isExpirationInPast = (epochSeconds: number): boolean => {
        if (!Number.isFinite(epochSeconds)) return false
        const endOfDayMs = (epochSeconds * 1000) + (24 * 60 * 60 * 1000) - 1
        return endOfDayMs < Date.now()
    }

    const resolveOptionLastPrice = (obj: any): number | null => {
        if (!obj || typeof obj !== 'object') return null
        // unwrap { call: {...} } shape from dev proxy
        const src = (obj && typeof obj.call === 'object') ? obj.call : obj
        const last = Number(src?.lastPrice ?? src?.lastTradePrice ?? src?.regularMarketPrice ?? 0)
        if (last > 0) return last
        const bid = Number(src?.bid ?? 0)
        const ask = Number(src?.ask ?? 0)
        if (bid > 0 && ask > 0) return (bid + ask) / 2
        if (ask > 0) return ask
        if (bid > 0) return bid
        return null
    }

    // Server endpoint fetch for a single option key (symbol|epoch|strike|right)
    const fetchOptionPriceForKeyServer = async (k: string): Promise<{ price: number | null; error: string | null }> => {
        try {
            const [symbol, epochStr, strikeStr, rightCharRaw] = k.split('|')
            const epoch = Number(epochStr)
            const strike = Number(strikeStr)
            const rightChar = (rightCharRaw || 'C').toUpperCase()
            if (!symbol || !Number.isFinite(epoch) || !Number.isFinite(strike) || !['C', 'P'].includes(rightChar)) {
                return { price: null, error: 'Invalid key' }
            }
            const expiration = new Date(epoch * 1000).toISOString().slice(0, 10)
            const req: GetOptionPriceRequest = {
                symbol: symbol.toUpperCase(),
                expiration: expiration as any,
                optionType: rightChar === 'C' ? OptionRightEnum.Call : OptionRightEnum.Put,
                strike: strike,
            }
            const resp = await api.post<OptionPriceResponse>('/optionposition/Price', req as any)
            if (resp.success && resp.data) {
                const px = (resp.data.lastPrice != null && Number(resp.data.lastPrice) > 0)
                    ? Number(resp.data.lastPrice)
                    : (resp.data.bid != null && resp.data.ask != null && Number(resp.data.bid) > 0 && Number(resp.data.ask) > 0)
                        ? (Number(resp.data.bid) + Number(resp.data.ask)) / 2
                        : (resp.data.ask != null && Number(resp.data.ask) > 0)
                            ? Number(resp.data.ask)
                            : (resp.data.bid != null && Number(resp.data.bid) > 0)
                                ? Number(resp.data.bid)
                                : null
                return { price: px, error: null }
            } else {
                return { price: null, error: resp.message || 'Server error' }
            }
        } catch (e: any) {
            return { price: null, error: e?.message || 'Network error' }
        }
    }

    // Yahoo proxy fetch for a single option key (symbol|epoch|strike|right)
    const fetchOptionPriceForKeyYF = async (k: string): Promise<{ price: number | null; error: string | null }> => {
        const [symbol, epochStr, strikeStr] = k.split('|')
        const url = `/__yf/options?symbol=${encodeURIComponent(symbol)}&date=${encodeURIComponent(epochStr)}&strike=${encodeURIComponent(strikeStr)}`
        try {
            const resp = await fetch(url)
            const data = await resp.json()
            if (!resp.ok) throw new Error(data?.error || `HTTP ${resp.status}`)
            const price = resolveOptionLastPrice(data)
            return { price, error: null }
        } catch (e: any) {
            return { price: null, error: e?.message || 'Failed' }
        }
    }

    const fetchOptionPriceForKey = async (k: string): Promise<{ price: number | null; error: string | null }> => {
        if (useServerOptionPrice.value) {
            return await fetchOptionPriceForKeyServer(k)
        }
        return await fetchOptionPriceForKeyYF(k)
    }

    // Build option key from position, falling back to explicit fields when code is absent or unparsable
    const getOptionKeyForPosition = (p: any): string | null => {
        try {
            const info = parseOptionKeyFromCode((p as any)?.code)
            if (info) return buildOptionKey(info.symbol, info.epoch, info.strike, info.right)
            const symbol = String((p?.ticker || p?.symbol || '')).trim().toUpperCase()
            const strike = Number(p?.strike)
            let rightChar: 'C' | 'P' | null = null
            const right = p?.right
            if (typeof right === 'number') rightChar = right === 1 ? 'C' : (right === 2 ? 'P' : null)
            else if (typeof right === 'string') {
                const r = right.toUpperCase()
                if (r.startsWith('C')) rightChar = 'C'
                else if (r.startsWith('P')) rightChar = 'P'
            }
            const expiryStr = typeof p?.expiry === 'string' ? p.expiry : (p?.expiry ? String(p.expiry) : null)
            if (!symbol || !Number.isFinite(strike) || !rightChar || !expiryStr) return null
            const epoch = Math.floor(new Date(expiryStr + 'T00:00:00Z').getTime() / 1000)
            if (!Number.isFinite(epoch)) return null
            const key = buildOptionKey(symbol, epoch, strike, rightChar)
            console.log('[Options] fallback key built', { symbol, strike, right: rightChar, expiry: expiryStr })
            return key
        } catch {
            return null
        }
    }

    const updateOptionPositionsPrices = (): void => {
        try {
            let updated = 0
            optionPositions.value.forEach((p: any) => {
                const key = getOptionKeyForPosition(p)
                if (!key) { p.price = null; return }
                const px = optionPrices.value.get(key)?.price ?? null
                const next = (typeof px === 'number' ? px : null)
                if (p.price !== next) updated++
                p.price = next
            })
            console.log('[Options] updateOptionPositionsPrices', { updated })
        } catch {}
    }

    const rebuildEnrichedOptionLotsWithPrices = (): void => {
        try {
            // reset all accounts' enriched option lots
            accounts.value.forEach(a => { a.enrichedOptionPositionLots = [] })
            let lotsPushed = 0
            let accountsTouched = new Set<number>()
            optionPositions.value.forEach((p: any) => {
                const key = getOptionKeyForPosition(p)
                const px = key ? optionPrices.value.get(key)?.price ?? null : null
                const lots: any[] = Array.isArray((p as any)?.lots)
                    ? (p as any).lots
                    : (Array.isArray((p as any)?.accounts) ? (p as any).accounts : (Array.isArray((p as any)?.optionPositionLots) ? (p as any).optionPositionLots : []))
                lots.forEach((l: any) => {
                    const accId = Number(l?.account?.id ?? l?.accountId ?? l?.accountID ?? 0)
                    if (!accId) return
                    const acc = accounts.value.find(a => a.id === accId)
                    if (!acc) return
                    const open = Number(l?.openCount ?? l?.remaining ?? l?.remainingQuantity ?? l?.quantityOpen ?? l?.quantity ?? 0) || 0
                    const multiplier = Number((p as any)?.multiplier ?? 100) || 100
                    const currentValue = (px != null && open > 0) ? (px * open * multiplier) : null
                    const lotCurrency = (p as any)?.currency || acc.currency || 'USD'
                    if (!Array.isArray(acc.enrichedOptionPositionLots)) acc.enrichedOptionPositionLots = []
                    acc.enrichedOptionPositionLots.push({ ...(l as any), currentValue, currency: lotCurrency })
                    lotsPushed++
                    accountsTouched.add(acc.id)
                })
            })
            console.log('[Options] rebuildEnrichedOptionLotsWithPrices', { lotsPushed, accountsTouched: accountsTouched.size })
        } catch {}
    }

    // One-off immediate fetch for option prices (dev proxy). Useful at startup to avoid waiting for the first interval tick
    const fetchOptionPricesOnce = async (): Promise<void> => {
        try {
            if (!optionPositions.value || optionPositions.value.length === 0) return
            const keySet = new Set<string>()
            optionPositions.value.forEach((p: any) => {
                const key = getOptionKeyForPosition(p)
                if (key) {
                    const parts = key.split('|')
                    const epoch = Number(parts[1])
                    if (!isExpirationInPast(epoch)) keySet.add(key)
                }
            })
            const keys = Array.from(keySet)
            if (keys.length === 0) return
            for (let i = 0; i < keys.length; i += BATCH_SIZE) {
                const batch = keys.slice(i, i + BATCH_SIZE)
                await Promise.all(batch.map(async (k) => {
                    const { price, error } = await fetchOptionPriceForKey(k)
                    optionPrices.value.set(k, { price, lastUpdated: new Date(), error })
                }))
            }
            rebuildEnrichedOptionLotsWithPrices()
            updateOptionPositionsPrices()
            recomputeUsdValuations()
        } catch {
            // silent
        }
    }


    const ensurePollingStarted = () => {
        if (!fmpApiKey) return
        // Markets
        // API refresh every hour (for holiday detection)
        if (!marketsInterval.value) {
            marketsInterval.value = setInterval(async () => {
                try {
                    const exchanges = new Set<string>()
                    trackedMarkets.value.forEach(ex => exchanges.add(ex.toUpperCase()))
                    await Promise.all(Array.from(exchanges).map(ex => updateExchangeStatus(ex)))
                } catch (e) {
                    console.error('[UltimateStore] Markets interval error:', e)
                }
            }, MARKETS_INTERVAL_MS)
        }
        // Client-side open/close check every minute (no API call)
        if (!localMarketCheckInterval.value) {
            localMarketCheckInterval.value = setInterval(() => {
                refreshMarketStatusLocally()
            }, LOCAL_MARKET_CHECK_MS)
        }
		// Stocks and Coins
        if (!stocksInterval.value) {
            stocksInterval.value = setInterval(async () => {
                try {
					const allSymbols = [
						...Array.from(stockPrices.value),
						...Array.from(coinPrices.value),
					]
                    if (allSymbols.length === 0) return
                    const uniqueExchanges = allSymbols.filter(s => s[1].exchange).map(s => s[1].exchange).filter(Boolean);
                    const exchangeOpen: Record<string, boolean | null> = {}
                    await Promise.all(uniqueExchanges.map(async ex => { exchangeOpen[ex] = await isExchangeOpen(ex) }))
                    const symbolsToFetch = allSymbols.filter(p => {
                        const ex = (p[1].exchange || '').toUpperCase()
                        if (!ex) return true
                        const open = exchangeOpen[ex]
                        if (open) return true
                        return false;
                    })
                    for (let i = 0; i < symbolsToFetch.length; i += BATCH_SIZE) {
                        const chunk = symbolsToFetch.slice(i, i + BATCH_SIZE)
						const chunkSymbols = chunk.map(s => s[0])
						const stockSymbols = chunkSymbols.filter(sym => stockPrices.value.has(sym))
						const coinSymbols = chunkSymbols.filter(sym => coinPrices.value.has(sym))
						if (stockSymbols.length) await fetchMultiplePrices(stockSymbols)
						if (coinSymbols.length) await fetchMultipleCoinPrices(coinSymbols)
                    }
                } catch (e) {
                    console.error('[UltimateStore] Stocks interval error:', e)
                }
            }, STOCKS_INTERVAL_MS)
        }
        // Options (dev: via Vite middleware /__yf/options)
        if (!optionsInterval.value) {
            optionsInterval.value = setInterval(async () => {
                try {
                    if (!optionPositions.value || optionPositions.value.length === 0) {
                        // nothing to fetch
                        return
                    }
                    // Derive unique option keys from current positions on each tick
                    const keySet = new Set<string>()
                    let parseOk = 0
                    let parseFail = 0
                    const failSamples: string[] = []
                    optionPositions.value.forEach((p: any) => {
                        const key = getOptionKeyForPosition(p)
                         if (key) {
                             const parts = key.split('|')
                             const epoch = Number(parts[1])
                             if (!isExpirationInPast(epoch)) { keySet.add(key); parseOk++ }
                         } else { parseFail++; const code = (p as any)?.code ?? `${p?.ticker} ${p?.expiry} ${p?.strike} ${p?.right}`; if (failSamples.length < 5) failSamples.push(String(code)) }
                    })
                    const keys = Array.from(keySet)
                    if (keys.length === 0) return
                    console.log('[Options] poll tick', { positions: optionPositions.value.length, keys: keys.length, parseOk, parseFail, failSamples })
                    for (let i = 0; i < keys.length; i += BATCH_SIZE) {
                        const batch = keys.slice(i, i + BATCH_SIZE)
                        console.log('[Options] fetch batch', { index: i, size: batch.length })
                        await Promise.all(batch.map(async k => {
                            const { price, error } = await fetchOptionPriceForKey(k)
                            optionPrices.value.set(k, { price, lastUpdated: new Date(), error })
                            console.log('[Options] price', { key: k, price, error })
                        }))
                    }
                    // after batch, rebuild option lot current values
                    rebuildEnrichedOptionLotsWithPrices()
                    // update per-position prices
                    updateOptionPositionsPrices()
                    // ensure account valuations reflect option lots
                    recomputeUsdValuations()
                } catch (e) {
                    // silent
                }
            }, OPTIONS_INTERVAL_MS)
            console.log('[Options] optionsInterval started', { intervalMs: OPTIONS_INTERVAL_MS })
            // Fire-and-forget initial fetch to avoid waiting for first tick
            if (!didInitialOptionsFetch.value) {
                didInitialOptionsFetch.value = true
                void fetchOptionPricesOnce()
            }
        }
        // Currencies
        if (!currenciesInterval.value) {

            currenciesInterval.value = setInterval(async () => {
                try {
                    console.log("ddddd", currencyPrices.value)
                    const allPairs = Array.from(currencyPrices.value)
                    if (allPairs.length === 0) return
                    for (let i = 0; i < allPairs.length; i += BATCH_SIZE) {
                        const chunk = allPairs.slice(i, i + BATCH_SIZE)
                        await fetchMultipleCurrencies(chunk.map(s => s[0]))
                    }
                } catch (e) {
                    console.error('[UltimateStore] Currencies interval error:', e)
                }
            }, CURRENCIES_INTERVAL_MS)
        }
        // Funds (poll last prices occasionally)
        if (!fundsInterval.value) {
            fundsInterval.value = setInterval(async () => {
                try {
                    const codes = Array.from(new Set(fundPositions.value.map(fp => fp.code).filter(Boolean)))
                    if (codes.length === 0) return
                    // Poll in batches
                    for (let i = 0; i < codes.length; i += BATCH_SIZE) {
                        await fetchMultipleFundLastPrices(codes.slice(i, i + BATCH_SIZE))
                    }
                } catch (e) {
                    console.error('[UltimateStore] Funds interval error:', e)
                }
            }, FUNDS_INTERVAL_MS)
        }
    }

    const isExchangeOpen = (exchange: string): boolean | null => {
        if (!exchange) return false
        const upper = exchange.toUpperCase()
        const status = exchangeStatuses.value.get(upper)
        if (status) {
            return status.isOpen;
        }
        return null;
    }

    async function loadAll(force = false): Promise<void> {
        try {
            await Promise.all([loadAccounts(), loadPositions(), loadCoinPositions(), loadFundPositions(), loadOptionPositions()])
        } catch (e: any) {
            //            error.value = e?.message || 'Network error'
        } finally {
            //          isLoading.value = false
        }
    }

    function setToken(token?: string) {
        api.setToken(token)
    }


    function cleanup(): void {
        accounts.value = []
        positions.value = []
		coinPositions.value = []
        fundPositions.value = []
        stockPrices.value.clear()
		coinPrices.value.clear()
        fundPrices.value.clear()
        currencyPrices.value.clear()
        exchangeStatuses.value.clear()
        trackedMarkets.value.clear()
        // stop price polling and clear trackers
        if (stocksInterval.value) { clearInterval(stocksInterval.value); stocksInterval.value = null }
        if (currenciesInterval.value) { clearInterval(currenciesInterval.value); currenciesInterval.value = null }
        if (marketsInterval.value) { clearInterval(marketsInterval.value); marketsInterval.value = null }
        if (localMarketCheckInterval.value) { clearInterval(localMarketCheckInterval.value); localMarketCheckInterval.value = null }
        if (fundsInterval.value) { clearInterval(fundsInterval.value); fundsInterval.value = null }
    }

    // Helpers: USD conversion and account valuation
    const getUsdRateForCurrency = (currencyCode: string | null | undefined): number | null => {
        if (!currencyCode) return null
        const code = currencyCode.toUpperCase()
        if (code === 'USD') return 1
        const pair = `USD${code}`
        const quote = currencyPrices.value.get(pair)
        if (quote && typeof quote.price === 'number' && quote.price > 0) return quote.price
        return null
    }

    const recomputeUsdValuations = (): void => {
        try {
            accounts.value.forEach((account) => {
                const cashLocal = account.amount ?? 0
                const cashRate = getUsdRateForCurrency(account.currency)
                const cashUsd = cashRate ? (cashLocal / cashRate) : (account.currency?.toUpperCase() === 'USD' ? cashLocal : 0)

                let stockUsd = 0
                const lots = account.enrichedStockPositionLots || []
                lots.forEach((lot) => {
                    if (lot.currentValue == null) return
                    const position = positions.value.find(p => p.id === lot.stockPositionId)
                    const posCurrency = position?.currency || account.currency || 'USD'
                    const rate = getUsdRateForCurrency(posCurrency)
                    const valueUsd = (posCurrency && posCurrency !== 'USD') ? (rate ? (lot.currentValue / rate) : 0) : lot.currentValue
                    stockUsd += valueUsd || 0
                })

                // Aggregate coin market value for this account in USD
                let coinUsd = 0
                let coinLocal = 0
                coinPositions.value.forEach((pos) => {
                    // coin lots include account info
                    const posCurrency = pos?.currency || 'USD'
                    const rate = getUsdRateForCurrency(posCurrency)
                    const priceLocal = pos.price || 0
                    pos.lots?.forEach((lot) => {
                        if (!lot.isOpen || lot.remainingQuantity <= 0) return
                        const lotAccountId = lot.account?.id || lot.accountId
                        if (lotAccountId !== account.id) return
                        const currentValueLocal = priceLocal * lot.remainingQuantity
                        const valueUsd = (posCurrency && posCurrency !== 'USD') ? (rate ? (currentValueLocal / rate) : 0) : currentValueLocal
                        coinUsd += valueUsd || 0
                        coinLocal += currentValueLocal || 0
                    })
                })

                // Aggregate fund market value for this account in USD
                let fundUsd = 0
                let fundLocal = 0
                fundPositions.value.forEach((pos) => {
                    const posCurrency = pos?.currency || 'USD'
                    const rate = getUsdRateForCurrency(posCurrency)
                    const priceLocal = pos.price || 0
                    pos.lots?.forEach((lot: any) => {
                        if (!lot.isOpen || lot.remainingQuantity <= 0) return
                        const lotAccountId = lot.account?.id || lot.accountId
                        if (lotAccountId !== account.id) return
                        const currentValueLocal = priceLocal * lot.remainingQuantity
                        const valueUsd = (posCurrency && posCurrency !== 'USD') ? (rate ? (currentValueLocal / rate) : 0) : currentValueLocal
                        fundUsd += valueUsd || 0
                        fundLocal += currentValueLocal || 0
                    })
                })

                // Aggregate option market value for this account in USD (from enriched option lots currentValue)
                let optionLocal = 0
                let optionUsd = 0
                const optLots = account.enrichedOptionPositionLots || []
                optLots.forEach((lot: any) => {
                    const currentValueLocal = Number(lot?.currentValue) || 0
                    if (currentValueLocal <= 0) return
                    const posCurrency = (lot?.currency || account.currency || 'USD')
                    const rate = getUsdRateForCurrency(posCurrency)
                    const valueUsd = (posCurrency && posCurrency !== 'USD') ? (rate ? (currentValueLocal / rate) : 0) : currentValueLocal
                    optionLocal += currentValueLocal
                    optionUsd += valueUsd || 0
                })

                account.cashUsd = cashUsd
                account.stockMarketValueUsd = stockUsd
                account.coinMarketValue = coinLocal
                account.coinMarketValueUsd = coinUsd
                account.fundMarketValue = fundLocal
                account.fundMarketValueUsd = fundUsd
                account.optionMarketValue = optionLocal
                account.optionMarketValueUsd = optionUsd
                account.totalValueUsd = cashUsd + stockUsd + coinUsd + fundUsd + optionUsd
            })
        } catch (e) {
            // Silent fail; valuations will recalc on next tick
        }
    }

    return {
        accounts,
        positions,
        optionPositions,
        coinPositions,
        fundPositions,
        stockPrices,
        coinPrices,
        fundPrices,
        currencyPrices,
        exchangeStatuses,
        trackedMarkets,
        loadAccounts,
        loadPositions,
        loadOptionPositions,
        loadCoinPositions,
        loadFundPositions,
        trackMultipleSymbols,
        trackMultipleCoinSymbols,
        fetchMultipleFundLastPrices,
        fetchMultiplePrices,
        fetchMultipleCoinPrices,
        updateExchangeStatus,
        fetchExchangeMarketHours,
        ensurePollingStarted,
        isExchangeOpen,
        cleanup,
        loadAll,
        setToken,
        // Option price source switch
        useServerOptionPrice,
        setUseServerOptionPrice,
        // UI: notes drawer control
        notesDrawerOpen,
        notesOwnerType,
        notesOwnerId,
        notes,
        notesTotal,
        notesIsLoading,
        notesError,
        notesPage,
        notesPageSize,
        openNotesDrawer,
        closeNotesDrawer,
        toggleNotesDrawer,
        loadNotes,
        createNote,
        noteTypeBadgeClassMap,
        getNoteTypeBadgeClasses
    }
})